import { createTheme, Theme } from "@mui/material/styles";

const UI_SCALE = 0.9; // 90% visual scale (like browser zoom 90%, but at 100%)

const lightTheme: Theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#0052cc",
      light: "#7DA1FF",
      dark: "#4A6CD4",
    },
    secondary: {
      main: "#9c27b0",
      light: "#ba68c8",
      dark: "#7b1fa2",
    },
    background: {
      default: "#f9fafb",
    },
  },
  typography: {
    fontFamily: '"Plus Jakarta Sans", sans-serif',
    // Base body font size (controls rem scaling across the app)
    fontSize: 14 * UI_SCALE,
    h1: {
      fontSize: "2rem",
      fontWeight: 700,
    },
    h2: {
      fontSize: "1.75rem",
      fontWeight: 700,
    },
    h3: {
      fontSize: "1.5rem",
      fontWeight: 600,
    },
    h4: {
      fontSize: "1.25rem",
      fontWeight: 600,
    },
    h5: {
      fontSize: "1.125rem",
      fontWeight: 600,
    },
    h6: {
      fontSize: "0.95rem",
      fontWeight: 600,
    },
    body1: {
      fontSize: "0.95rem",
    },
    body2: {
      fontSize: "0.85rem",
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          fontSize: `${UI_SCALE * 100}%`,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: "7px",
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: "7px",

            "& input": {
              padding: "0.75rem 1rem"
            },


          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderWidth: "1px",
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          "& .MuiSelect-select": {
            padding: "0.75rem 1rem"
          }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: "10px",
          boxShadow: "0 5px 15px rgba(0, 0, 0, 0.05)"
        }
      }
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize: "0.875rem", // 14px equivalent, uses rem
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          "&.Mui-selected": {
            "& .MuiListItemIcon-root": {
              color: "white !important",
            },
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: "rgb(42, 53, 71)",
          minWidth: "42px",
        },
      },
    },
  },
});

const darkTheme: Theme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "rgb(255, 255, 255)",
      light: "#e3f2fd",
      dark: "#1976d2",
    },
    secondary: {
      main: "#ff4081",
      light: "#ff80ab",
      dark: "#c60055",
    },
    background: {
      default: "#181818",
      paper: "#232323",
    },
    text: {
      primary: "#f5f5f5",
      secondary: "#bdbdbd",
    },
  },
  typography: {
    fontFamily: '"Plus Jakarta Sans", sans-serif',
    fontSize: 14 * UI_SCALE,
    h1: {
      fontSize: "2rem",
      fontWeight: 700,
    },
    h2: {
      fontSize: "1.75rem",
      fontWeight: 700,
    },
    h3: {
      fontSize: "1.5rem",
      fontWeight: 600,
    },
    h4: {
      fontSize: "1.25rem",
      fontWeight: 600,
    },
    h5: {
      fontSize: "1.125rem",
      fontWeight: 600,
    },
    h6: {
      fontSize: "0.95rem",
      fontWeight: 600,
    },
    body1: {
      fontSize: "0.95rem",
    },
    body2: {
      fontSize: "0.85rem",
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          fontSize: `${UI_SCALE * 100}%`,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: "7px",
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: "7px",

            "& input": {
              padding: "0.75rem 1rem"
            },

          },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize: "0.875rem",
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          "&.Mui-selected": {
            "& .MuiListItemIcon-root": {
              color: "white !important",
            },
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: "rgb(200, 200, 200)",
          minWidth: "42px",
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          "& .MuiSelect-select": {
            padding: "0.75rem 1rem"
          }
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderWidth: "1px",
          },
        },
      },
    },
  },
});

export function getTheme(mode: "light" | "dark"): Theme {
  return mode === "dark" ? darkTheme : lightTheme;
}

export { lightTheme, darkTheme };
